import type { ChangeEvent } from "react";
import { useCallback, useEffect, useState } from "react";

import { CheckboxList } from "@fronterahealth/frontera-ui-components";

import { FieldLayout } from "@components/BlurBasedFormFields/InlineFormFields/FieldLayout";
import type { InlineFormFieldProps } from "@components/BlurBasedFormFields/InlineFormFields/types";

export const InlineCheckboxListField = <T extends Record<string, unknown>>({
  value,
  fieldKey,
  onSave,
  title,
  className,
  checkboxItems,
}: Omit<Extract<InlineFormFieldProps<T>, { inputType: "checkboxList" }>, "inputType">) => {
  const ids = Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
  const [selectedIds, setSelectedIds] = useState<string[]>(ids);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const next = Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
    setSelectedIds(next);
  }, [value]);

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const { value: id, checked } = e.target;
      const updated = checked ? [...selectedIds, id] : selectedIds.filter((x) => x !== id);
      setSelectedIds(updated);
      setError(null);
      try {
        const result = await onSave({ [fieldKey]: updated } as Partial<T>);
        if (!result.success && result.error) setError(result.error);
      } catch (err) {
        console.error("Error saving field:", err);
        setSelectedIds(ids);
        setError("Failed to save changes");
      }
    },
    [fieldKey, onSave, selectedIds, ids],
  );

  return (
    <FieldLayout title={title} className={className}>
      <CheckboxList
        title=""
        key={selectedIds.join("-")}
        legend={`${title} choice`}
        name={fieldKey as string}
        items={checkboxItems.map((item) => ({ ...item, defaultChecked: selectedIds.includes(item.id) }))}
        onChange={handleChange}
        orientation="vertical"
        errorMessage={error ?? undefined}
      />
    </FieldLayout>
  );
};
